import { Component, TemplateRef, computed, inject, signal, viewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs/operators';
import { TextSearchComponent } from 'src/app/shared/components/text-search/text-search.component';
import { CardComponent, ExpandListComponent, ExpandListConfiguration, ScreenSizeEnum } from 'ok-angular-shared/components';
import { EnvironmentLoaderService } from '../../../../shared/services/environment-loader.service';
import { Organization, OrganizationDisplay } from '../../organization-models';
import { Router, RouterLink } from '@angular/router';
import { AppState } from 'src/app/app.state';
import { OrganizationType } from '../../../../shared/enums/organization-type.enum';
import { RequiresFeatureRoleDirective } from '../../../../shared/directives/requires-feature-role.directive';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PagerBase } from 'src/app/shared/models/pagerBase';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { OrganizationDetailsComponent } from '../organization-details/organization-details.component';
import { AuthService } from 'src/app/core/auth/auth.service';
import { Permission } from 'src/app/shared/enums/permission.enum';
import { OrganizationLogoComponent } from './organization-logo/organization-logo.component';
import { OrganizationListDialogService } from './organization-list-dialog.service';
import { OrganizationListStateService } from './organization-list-state.service';

export function createOrganizationActionItems(deps: {
  dialogService: OrganizationListDialogService;
  authService: AuthService;
  router: Router;
  roles: Record<string, string>;
  publicPortalName: string;
}) {
  return {
    createDepartment: {
      svgIcon: 'add_home',
      label: 'Opret afdeling',
      permission: Permission.DepartmentWrite,
      action: (item?: Organization) => {
        if (item) deps.dialogService.openCreateDepartmentDialog(item as OrganizationDisplay);
      },
      hiddenFn: () => !deps.authService.hasRole(deps.roles[Permission.DepartmentWrite]),
    },
    createUser: {
      icon: { name: 'user-add-s' },
      label: 'Opret bruger',
      permission: Permission.ChargingUserCreate,
      action: (item?: Organization) => {
        if (item) deps.dialogService.showCreateUserMenu(item as OrganizationDisplay);
      },
      hiddenFn: () => !deps.authService.hasRole(deps.roles[Permission.ChargingUserCreate]),
    },
    editAccessGroups: {
      icon: { name: 'users-s' },
      label: 'Rediger adgangsgrupper',
      permission: Permission.AccessGroupWrite,
      action: (item?: Organization) => {
        if (item) deps.router.navigate(['/access-groups'], { queryParams: { organizationId: item.id } });
      },
      hiddenFn: () => !deps.authService.hasRole(deps.roles[Permission.AccessGroupWrite]),
    },
    downloadReport: {
      icon: { name: 'download-s' },
      label: 'Download rapport',
      permission: Permission.CustomerReportDownload,
      action: (item?: Organization) => {
        if (item) deps.dialogService.openOrganizationDownloadReportDialog(item as OrganizationDisplay);
      },
      hiddenFn: () => !deps.authService.hasRole(deps.roles[Permission.CustomerReportDownload]),
    },
    editDiscountType: {
      icon: 'percent_discount',
      label: 'Rediger rabattype',
      permission: Permission.OrganizationDiscountTypeWrite,
      action: (item?: Organization) => {
        if (item) deps.dialogService.openEditDiscountTypeModal(item as OrganizationDisplay);
      },
      hiddenFn: () => !deps.authService.hasRole(deps.roles[Permission.OrganizationDiscountTypeWrite]),
    },
    kickback: {
      svgIcon: 'handshake',
      label: 'Kickback-politikker',
      permission: Permission.KickbackWrite,
      action: (item?: Organization) => {
        if (item) deps.dialogService.openKickbackModal(item as OrganizationDisplay);
      },
      hiddenFn: () => !deps.authService.hasRole(deps.roles[Permission.KickbackWrite]),
    },
    imitate: {
      icon: { name: 'users-s' },
      label: `Imiter organisation på ${deps.publicPortalName}`,
      permission: Permission.ImitationStartOrStop,
      action: (item?: Organization) => {
        if (item) deps.dialogService.imitateOrganization(item as OrganizationDisplay);
      },
      hiddenFn: () => !deps.authService.hasRole(deps.roles[Permission.ImitationStartOrStop]),
    },
    departmentGroups: {
      svgIcon: 'layers',
      label: 'Administrér afdelingsgrupper',
      permission: Permission.DepartmentGroupRead,
      action: (item?: Organization) => {
        if (item) deps.dialogService.openDepartmentGroupAddOrEditDialog(item as OrganizationDisplay);
      },
      hiddenFn: () => !deps.authService.hasRole(deps.roles[Permission.DepartmentGroupRead]),
    },
    manageUsers: {
      icon: { name: 'user-s' },
      label: 'Administrér portalbrugere',
      permission: Permission.UserRead,
      action: (item?: Organization) => {
        if (item) deps.router.navigate(['/users'], { queryParams: { organizationId: item.id } });
      },
      hiddenFn: () => !deps.authService.hasRole(deps.roles[Permission.UserRead]),
    },
    editOrganization: {
      icon: { name: 'edit-s' },
      label: 'Rediger organisation',
      permission: Permission.OrganizationWrite,
      action: (item?: Organization) => {
        if (item) deps.dialogService.openEditOrganizationDialog(item as OrganizationDisplay);
      },
      hiddenFn: () => !deps.authService.hasRole(deps.roles[Permission.OrganizationWrite]),
    },
    deleteOrganization: {
      icon: { name: 'trash-s' },
      label: 'Slet organisation',
      permission: Permission.OrganizationDelete,
      action: (item?: Organization) => {
        if (item) deps.dialogService.openDeleteOrganizationDialog(item as OrganizationDisplay);
      },
      hiddenFn: () => !deps.authService.hasRole(deps.roles[Permission.OrganizationDelete]),
    },
  };
}

@Component({
  selector: 'adm-organization-list',
  templateUrl: './organization-list.component.html',
  styleUrls: ['./organization-list.component.scss'],
  imports: [
    RequiresFeatureRoleDirective,
    RouterLink,
    TextSearchComponent,
    MatMenu,
    MatMenuTrigger,
    MatPaginator,
    MatButton,
    MatMenuItem,
    CardComponent,
    ExpandListComponent,
    OrganizationDetailsComponent,
    OrganizationLogoComponent,
    MatTooltip,
  ],
  providers: [OrganizationListDialogService, OrganizationListStateService],
})
export class OrganizationListComponent extends PagerBase {
  private environmentLoaderService = inject(EnvironmentLoaderService);
  private router = inject(Router);
  appState = inject(AppState);
  authService = inject(AuthService);
  dialogService = inject(OrganizationListDialogService);
  state = inject(OrganizationListStateService);
  public orgLogoColumnTemplate = viewChild<TemplateRef<OrganizationLogoComponent>>('orgLogoColumn');

  rowClicked$ = new Subject<OrganizationDisplay>();
  public idTagHeaderTemplate = viewChild<TemplateRef<unknown>>('idTagHeaderTemplate');
  public publicPortalName = this.environmentLoaderService.config.publicPortalName;
  public OrganizationType = OrganizationType;
  public roles = this.environmentLoaderService.virtualRoles;

  public actionItems = createOrganizationActionItems({
    dialogService: this.dialogService,
    authService: this.authService,
    router: this.router,
    roles: this.roles,
    publicPortalName: this.publicPortalName,
  });

  constructor() {
    super();

    this.rowClicked$
      .pipe(
        distinctUntilChanged(),
        tap(organization => {
          const itemIndex = this.state.organizationsState().items.findIndex(s => s.id === organization.id);
          const currentExpandedState = this.state.organizationsState().items[itemIndex].isExpanded;
          const updatedOrg = {
            ...organization,
            isExpanded: !currentExpandedState,
          } as OrganizationDisplay;
          this.state.organizationsState.update(current => {
            const newItems = [...current.items];
            //replace item in list with new one
            newItems[itemIndex] = {
              ...updatedOrg,
            };
            return {
              ...current,
              items: newItems,
            };
          });
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  public searchInput(source: Observable<string>) {
    source.subscribe(searchTerm => {
      this.state.filters.update(f => ({ ...f, query: searchTerm, page: { length: 0, pageIndex: 0, pageSize: f.page.pageSize } }));
    });
  }

  public pageSwitch(event: PageEvent) {
    this.state.filters.update(f => ({ ...f, page: event }));
  }

  // Config for expand list
  public config = computed<ExpandListConfiguration<OrganizationDisplay>>(() => ({
    classFn: row => (row.hasHeadquarter ? '' : 'warning'),
    tooltipFn: row => (row.hasHeadquarter ? '' : 'Organisation mangler afdeling markeret som hovedkontor!'),
    badgeFn: row => (row.hasHeadquarter ? '' : '!'),
    onRowClick: row => {
      this.rowClicked$.next(row);
    },
    expandDisabled: false,
    columns: [
      {
        field: 'compactLogoLink',
        header: '',
        width: '50px',
        sortField: undefined,
        displayTemplate: this.orgLogoColumnTemplate(),
        disableSort: true,
      },
      { field: 'name', header: 'Navn', width: '2fr', sortField: 'Name' },
      { field: 'translatedOrganizationType', header: 'Type', width: '1fr', sortField: 'Type' },
      { field: 'identification', header: 'G4 kundenr.', width: '1fr', sortField: 'Identification', visibleFrom: ScreenSizeEnum.md },
      { field: 'idTagGroupName' as keyof OrganizationDisplay, header: this.idTagHeaderTemplate()!, width: '1fr', sortField: 'IdTagGroup' },
      { field: 'departmentCount', header: 'Afdeling Antal', width: '1fr', sortField: 'DepartmentCount', visibleFrom: ScreenSizeEnum.md },
    ],
    actions: Object.values(this.actionItems),
  }));

  public goToUsers(organization: OrganizationDisplay) {
    this.router.navigate(['/users'], {
      queryParams: { organizationId: organization.id },
    });
  }

  public goToAccessGroups(organization: OrganizationDisplay) {
    this.router.navigate(['/access-groups'], {
      queryParams: {
        organizationId: organization.id,
      },
    });
  }

  public departmentDeleted(organization: OrganizationDisplay) {
    this.state.refreshSingleOrganization$.next(organization.id);
  }
}
